import { useEffect, useRef, useState } from 'react';
import './confirm.css';

/**
 * The confirmation every consequential action goes through.
 *
 * NOT a nicety. The operator's rule for this phase: no writes without a
 * confirmation for destructive types — cancel, suspend, signup approve — and
 * the halt switch, which stops or starts every outbound email in the system.
 *
 * `typeToConfirm` asks for a word to be typed. That is reserved for the few
 * actions where a misplaced click changes somebody's phone service; using it
 * everywhere would train the operator to type it without reading.
 */
export interface ConfirmRequest {
  title: string;
  detail?: string;
  confirmLabel: string;
  tone?: 'normal' | 'danger';
  typeToConfirm?: string;
  askReason?: boolean;
  reasonLabel?: string;
  /** The confirm stays disabled until a reason is typed. */
  reasonRequired?: boolean;
  reasonPlaceholder?: string;
  onConfirm: (reason: string) => void | Promise<void>;
}

export function Confirm({ request, onClose }: { request: ConfirmRequest | null; onClose: () => void }) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const first = useRef<HTMLInputElement | HTMLButtonElement>(null);

  useEffect(() => {
    setTyped(''); setReason(''); setBusy(false);
    if (request) first.current?.focus();
  }, [request]);

  useEffect(() => {
    if (!request) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [request, onClose]);

  if (!request) return null;

  const needsTyping = Boolean(request.typeToConfirm);
  const ready = (!needsTyping || typed.trim() === request.typeToConfirm)
    && (!request.reasonRequired || reason.trim() !== '');

  const go = async () => {
    if (!ready || busy) return;
    setBusy(true);
    try { await request.onConfirm(reason.trim()); onClose(); } finally { setBusy(false); }
  };

  return (
    <div className="confirm-veil" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
      <div className="confirm">
        <h2 id="confirm-title">{request.title}</h2>
        {request.detail && <p>{request.detail}</p>}

        {request.askReason && (
          <label className="confirm-field">
            <span>{request.reasonLabel ?? 'Reason'}</span>
            <input
              id="confirm-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={request.reasonPlaceholder ?? 'recorded on the card'}
            />
          </label>
        )}

        {needsTyping && (
          <label className="confirm-field">
            <span>Type <code>{request.typeToConfirm}</code> to confirm</span>
            <input
              id="confirm-typed"
              ref={first as React.RefObject<HTMLInputElement>}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') go(); }}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
        )}

        <div className="confirm-actions">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="button"
            className={request.tone === 'danger' ? 'btn-danger' : 'btn-primary'}
            onClick={go}
            disabled={!ready || busy}
            ref={!needsTyping ? (first as React.RefObject<HTMLButtonElement>) : undefined}
          >
            {busy ? 'Working…' : request.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
